import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { adminUserApi } from '../../lib/api';
import { toast } from 'sonner';
import { Shield, Loader2, Save, Palette, FileText, GraduationCap, Mail, Server, Users } from 'lucide-react';

const SECTION_META = {
    appearance: { label: 'Darstellung', desc: 'Design, Logos, Schriften, Widget', Icon: Palette },
    documents: { label: 'Dokumente & Belege', desc: 'PDF-Vorlagen, Belege, Rechtliches', Icon: FileText },
    training: { label: 'Lizenzen & Seminare', desc: 'Vorkenntnisse, Verlängerungsregeln', Icon: GraduationCap },
    mail: { label: 'Mail', desc: 'SMTP, Mail-Vorlagen, Signatur, Erinnerungen', Icon: Mail },
    system: { label: 'System', desc: 'Version, Updates, Audit-Log, Dropbox, Auto-Logout', Icon: Server },
    'user-management': { label: 'Benutzer & Rechte', desc: 'Reserviert für Super-Admin', Icon: Users, locked: true },
    'admin-permissions': { label: 'Berechtigungs-Matrix', desc: 'Reserviert für Super-Admin', Icon: Shield, locked: true },
};

export function AdminPermissionsCard() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [allowed, setAllowed] = useState(new Set());
    const [knownTabs, setKnownTabs] = useState([]);

    const load = async () => {
        setLoading(true);
        try {
            const r = await adminUserApi.getPermissions();
            setAllowed(new Set(r.data?.allowed_tabs || []));
            setKnownTabs(r.data?.known_tabs || []);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const toggle = (key) => {
        const meta = SECTION_META[key];
        if (meta?.locked) return;
        setAllowed(prev => {
            const n = new Set(prev);
            if (n.has(key)) n.delete(key); else n.add(key);
            return n;
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            await adminUserApi.setPermissions(Array.from(allowed));
            toast.success('Berechtigungen gespeichert');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Fehler');
        } finally { setSaving(false); }
    };

    return (
        <Card data-testid="admin-permissions-card">
            <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                    <Shield className="h-5 w-5" /> Admin-Berechtigungen
                </CardTitle>
                <CardDescription>
                    Steuern Sie, welche Settings-Bereiche reguläre Admins (kein Super-Admin) sehen dürfen.
                    Standard: alles gesperrt – Sie geben gezielt frei.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                {loading ? (
                    <div className="text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Lade…</div>
                ) : (
                    <>
                        <ul className="flex flex-col divide-y border rounded-lg overflow-hidden">
                            {knownTabs.map(key => {
                                const meta = SECTION_META[key] || { label: key, desc: '', Icon: Shield };
                                const Icon = meta.Icon;
                                const isOn = allowed.has(key);
                                return (
                                    <li key={key} className="px-3 py-3 flex items-center gap-3 bg-white" data-testid={`perm-row-${key}`}>
                                        <Icon className={`h-4 w-4 ${meta.locked ? 'text-muted-foreground' : 'text-primary'}`} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900">{meta.label}</p>
                                            <p className="text-xs text-muted-foreground">{meta.desc}</p>
                                        </div>
                                        {meta.locked ? (
                                            <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded bg-violet-100 text-violet-700">Super-Admin</span>
                                        ) : (
                                            <Switch checked={isOn} onCheckedChange={() => toggle(key)} data-testid={`perm-toggle-${key}`} />
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                        <div className="flex justify-end">
                            <Button onClick={save} disabled={saving} data-testid="perm-save">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                Berechtigungen speichern
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
